import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Moon, Sun, Menu } from 'lucide-react';
import PotredPathDrawing from './PotredPathDrawing.jsx';
import PotredStroke from './PotredStroke.jsx';
import FontSizeControls from './FontSizeControls.jsx';

// Motion variants for mobile menu animation - matches Next.js exactly
const sidebarVariants = {
  open: {
    opacity: 1,
    height: "auto",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      restDelta: 2,
    },
  },
  closed: {
    opacity: 0,
    height: 0,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 40,
    },
  },
};

const navVariants = {
  open: {
    transition: { staggerChildren: 0.07, delayChildren: 0.2 },
  },
  closed: {
    transition: { staggerChildren: 0.05, staggerDirection: -1 },
  },
};

const itemVariants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  closed: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
};

// Hamburger menu path animation variants
const pathVariants = {
  closed: { d: "M 2 2.5 L 20 2.5" },
  open: { d: "M 3 16.5 L 17 2.5" },
};

const middlePathVariants = {
  closed: { opacity: 1 },
  open: { opacity: 0 },
};

const bottomPathVariants = {
  closed: { d: "M 2 16.346 L 20 16.346" },
  open: { d: "M 3 2.5 L 17 16.346" },
};

const Navigation = ({ 
  siteName = "Potred",
  navigation = [],
  searchEnabled = false,
  membersEnabled = false 
}) => {
  const [isDark, setIsDark] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const logoRef = useRef(null);

  // Handle theme toggle
  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem('theme');
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      const shouldBeDark = savedTheme === 'dark' || (savedTheme === null && prefersDark);
      
      setIsDark(shouldBeDark);
      
      if (shouldBeDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    checkTheme();
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkTheme);
    
    return () => mediaQuery.removeEventListener('change', checkTheme);
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    
    if (newIsDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav id="site-navigation" className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-slate-950/90 backdrop-blur-md border-b border-gray-200/50 dark:border-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <div id="nav-logo" className="flex items-center">
            <div className="flex-shrink-0">
              <a href="/" className="flex items-center gap-2">
                <motion.div
                  whileHover={{
                    scale: [null, 1.1, 1.6],
                    transition: {
                      duration: 0.3,
                      times: [0, 0.6, 1],
                      ease: ["easeInOut", "easeOut"],
                    },
                  }}
                >
                  <PotredPathDrawing 
                    ref={logoRef}
                    className="w-8 h-8 text-orange-600 dark:text-orange-600"
                    enablePeriodicAnimation={true}
                    animateOnMount={false}
                  />
                </motion.div>
                <div className="w-32 h-8 sm:w-36 sm:h-9 flex items-center">
                  <PotredStroke 
                    text="Potred"
                    className="text-orange-600 dark:text-orange-400"
                    stroke={2}
                    duration={2.2}
                    size="medium"
                    responsive={true}
                  />
                </div>
              </a>
            </div>
          </div>

          {/* Desktop Navigation - Center */}
          <div id="nav-desktop-menu" className="hidden md:flex flex-1 justify-center max-w-2xl mx-auto">
            <div className="flex items-baseline space-x-8 lg:space-x-12">
              {navigation.length > 0 ? (
                navigation.map((item, index) => (
                  <a
                    key={index}
                    href={item.url}
                    className="text-orange-600 dark:text-orange-400 hover:text-red-600 dark:hover:text-red-400 px-3 py-2 rounded-md text-base font-medium font-iansui transition-colors duration-200"
                  >
                    {item.label}
                  </a>
                ))
              ) : (
                <div className="text-gray-500 dark:text-gray-400 text-sm">
                  {/* No navigation items found */}
                </div>
              )}
            </div>
          </div>

          {/* Font Size Controls, Theme Toggle & Mobile Menu Button */}
          <div id="nav-controls" className="flex items-center space-x-4">
            {/* Font Size Controls - Post/Story Pages Only */}
            <FontSizeControls />
            {/* Theme Toggle with Wildcard Keyframes Animation & Glare Effect */}
            <motion.button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors duration-200 relative overflow-hidden"
              aria-label="Toggle theme"
              whileHover={{
                scale: [null, 1.1, 1.6],
                transition: {
                  duration: 0.3,
                  times: [0, 0.6, 1],
                  ease: ["easeInOut", "easeOut"],
                },
              }}
              whileTap={{
                scale: 0.95,
                boxShadow: "0 0 0 3px rgba(234, 88, 12, 0.6)",
                transition: { duration: 0.1 }
              }}
              transition={{
                duration: 0.3,
                ease: "easeOut",
              }}
            >
              {isDark ? (
                <Sun className="w-6 h-6 text-orange-500 dark:text-orange-400 relative z-10" />
              ) : (
                <Moon className="w-6 h-6 text-orange-600 dark:text-orange-400 relative z-10" />
              )}
              
              {/* Orange Glare/Flash Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-400 to-transparent opacity-0"
                style={{ background: 'linear-gradient(90deg, transparent, #ff8904, transparent)' }}
                initial={{ opacity: 0, x: '-100%' }}
                whileHover={{
                  opacity: [0, 0.5, 0],
                  x: ['100%', '100%', '200%'],
                  transition: {
                    duration: 0.3,
                    times: [0, 0.5, 1],
                    ease: "easeOut",
                  },
                }}
              />
            </motion.button>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <motion.button
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors duration-200 relative z-50"
                aria-label="Toggle mobile menu"
                initial={false}
                animate={isMobileMenuOpen ? "open" : "closed"}
                whileHover={{
                  scale: [null, 1.1, 1.6],
                  transition: {
                    duration: 0.3,
                    times: [0, 0.6, 1],
                    ease: ["easeInOut", "easeOut"],
                  },
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <motion.path
                    fill="transparent"
                    strokeWidth="3"
                    stroke="currentColor"
                    strokeLinecap="round"
                    className="text-orange-600 dark:text-orange-400"
                    variants={pathVariants}
                  />
                  <motion.path
                    d="M 2 9.423 L 20 9.423"
                    fill="transparent"
                    strokeWidth="3"
                    stroke="currentColor"
                    strokeLinecap="round"
                    className="text-orange-600 dark:text-orange-400"
                    variants={middlePathVariants}
                    transition={{ duration: 0.1 }}
                  />
                  <motion.path
                    fill="transparent"
                    strokeWidth="3"
                    stroke="currentColor"
                    strokeLinecap="round"
                    className="text-orange-600 dark:text-orange-400"
                    variants={bottomPathVariants}
                  />
                </svg>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <motion.div
        id="nav-mobile-menu"
        className="md:hidden absolute top-full left-0 right-0 z-40"
        initial={false}
        animate={isMobileMenuOpen ? "open" : "closed"}
        style={{ pointerEvents: isMobileMenuOpen ? "auto" : "none" }}
      >
        <motion.div
          className="bg-white/95 dark:bg-slate-950/95 backdrop-blur-md border-t border-gray-200/50 dark:border-slate-800/50"
          variants={sidebarVariants}
        >
          <motion.nav
            className="px-2 pt-2 pb-3"
            variants={navVariants}
          >
            <motion.div className="flex flex-col space-y-1">
              {navigation.length > 0 ? (
                navigation.map((item, index) => (
                  <motion.div key={index} variants={itemVariants}>
                    <a
                      href={item.url}
                      className="text-orange-600 dark:text-orange-400 hover:text-red-600 dark:hover:text-red-400 block px-3 py-2 rounded-md text-lg font-medium font-iansui transition-colors duration-200"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  </motion.div>
                ))
              ) : (
                <motion.div variants={itemVariants} className="text-center py-4">
                  <div className="text-gray-500 dark:text-gray-400 text-sm">
                    No navigation items found
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.nav>
        </motion.div>
      </motion.div>
    </nav>
  );
};

export default Navigation;